/**
 * HelpLinkRenderer — Renders plain-text strings containing markdown-style links.
 *
 * Parses `[text](url)` patterns in help content strings and renders them as
 * clickable MUI links. Internal routes use navigate() and auto-close the
 * help drawer; mailto/tel links open natively; external URLs open in new tabs.
 */

import React, { useMemo } from 'react';
import { Link as MuiLink } from '@mui/material';
import { useNavigate } from 'react-router-dom';

interface HelpLinkRendererProps {
  /** The text string that may contain `[text](url)` links */
  text: string;
  /** Called after navigating to an internal route (e.g. to close the help drawer) */
  onNavigate?: () => void;
}

/**
 * Returns true if the href is an internal app route (not a static asset).
 */
function isInternalRoute(href: string): boolean {
  if (!href.startsWith('/')) return false;
  // Reject protocol-relative URLs (//example.com) — these are external
  if (href.startsWith('//')) return false;
  // Exclude image/static asset paths
  if (/^\/help-screenshots\//i.test(href)) return false;
  if (/\.(png|jpe?g|gif|svg|webp|mp4|webm|ogg|mov|pdf)(\?|$)/i.test(href)) return false;
  return true;
}

/**
 * Returns true if the href uses a safe scheme for rendering as an external link.
 * Blocks javascript:, data:, vbscript:, and other dangerous schemes.
 */
function isSafeExternalUrl(href: string): boolean {
  return /^https?:\/\//i.test(href);
}

/**
 * Returns true if the click event is a plain left-click without modifier keys.
 * Modified clicks (ctrl/cmd/shift/middle-click) should use default browser
 * behavior (open in new tab, etc.).
 */
function isUnmodifiedLeftClick(e: React.MouseEvent): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

export const HelpLinkRenderer: React.FC<HelpLinkRendererProps> = ({
  text,
  onNavigate,
}) => {
  const navigate = useNavigate();

  const parts = useMemo(() => {
    const result: Array<{ type: 'text'; value: string } | { type: 'link'; text: string; href: string }> = [];
    let lastIndex = 0;

    // Create a fresh RegExp instance per render to avoid shared mutable /g state
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    let match: RegExpExecArray | null;

    while ((match = linkRegex.exec(text)) !== null) {
      // Add preceding text
      if (match.index > lastIndex) {
        result.push({ type: 'text', value: text.slice(lastIndex, match.index) });
      }
      result.push({ type: 'link', text: match[1], href: match[2] });
      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      result.push({ type: 'text', value: text.slice(lastIndex) });
    }

    return result;
  }, [text]);

  // Fast path: no links found, return plain text
  if (parts.length === 1 && parts[0].type === 'text') {
    return <>{text}</>;
  }

  const handleInternalClick = (href: string) => (e: React.MouseEvent) => {
    // Only intercept unmodified left-clicks for SPA navigation.
    // Ctrl/Cmd+click, middle-click, etc. should use default browser behavior.
    if (!isUnmodifiedLeftClick(e)) return;
    e.preventDefault();
    // Auto-close the drawer first, then navigate
    onNavigate?.();
    navigate(href);
  };

  return (
    <>
      {parts.map((part, i) => {
        if (part.type === 'text') {
          return <React.Fragment key={i}>{part.value}</React.Fragment>;
        }

        const { text: linkText, href } = part;

        // Internal app route — render as a real anchor with actual href
        if (isInternalRoute(href)) {
          return (
            <MuiLink
              key={i}
              href={href}
              onClick={handleInternalClick(href)}
              sx={{
                cursor: 'pointer',
                verticalAlign: 'baseline',
                fontWeight: 500,
                fontSize: 'inherit',
                lineHeight: 'inherit',
                textDecorationStyle: 'dotted',
              }}
            >
              {linkText}
            </MuiLink>
          );
        }

        // mailto: link
        if (href.startsWith('mailto:')) {
          return (
            <MuiLink key={i} href={href} sx={{ fontSize: 'inherit', lineHeight: 'inherit' }}>
              {linkText}
            </MuiLink>
          );
        }

        // tel: link
        if (href.startsWith('tel:')) {
          return (
            <MuiLink key={i} href={href} sx={{ fontSize: 'inherit', lineHeight: 'inherit' }}>
              {linkText}
            </MuiLink>
          );
        }

        // External URL — only allow safe schemes (https?://)
        if (isSafeExternalUrl(href)) {
          return (
            <MuiLink key={i} href={href} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 'inherit', lineHeight: 'inherit' }}>
              {linkText}
            </MuiLink>
          );
        }

        // Unsafe/unknown scheme — render as plain text
        return <React.Fragment key={i}>{linkText}</React.Fragment>;
      })}
    </>
  );
};

export default HelpLinkRenderer;
